// Phase 1 (ADR-0024) admin form to author a new DRAFT adventure.
//
// Shape:
//   - Team picker (required)
//   - Name (required)
//   - Dates (starts_at, ends_at) — defaults to a 14-day window
//     starting today (matches the team's 2-week sprint cadence)
//   - Jira sprint id (optional)
//   - Completion grace days (0 default, max 7)
//   - Commitment list (optional — Phase 2 will add goal authoring;
//     for Phase 1 the commitment is the only commitment snapshot
//     input and can be edited via the detail page after create)
//
// On submit: POST /api/admin/adventures, redirect to detail page.

import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../../lib/api';
import { adventuresApi } from './api';
import type { JiraSprintSummary } from './api';
import { ApiError } from '../../../lib/api';
import type { AdventureCommitmentTicket } from './types';

function defaultStart(): string {
  // Local datetime-local string for the starts_at input. The
  // conversion to ISO UTC happens on submit.
  const d = new Date();
  d.setSeconds(0, 0);
  // Round to nearest minute so the displayed value matches what
  // the API will store.
  return toLocalInput(d);
}

function defaultEnd(): string {
  // 14-day sprint cadence (matches the team's actual practice
  // per ADR-0024 §1).
  const d = new Date();
  d.setDate(d.getDate() + 14);
  d.setSeconds(0, 0);
  return toLocalInput(d);
}

function toLocalInput(d: Date): string {
  // Format YYYY-MM-DDTHH:mm in the operator's local timezone.
  // new Date().toISOString().slice(0,16) would give UTC and
  // cause off-by-N-hours in the wall TV display.
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localToIso(local: string): string {
  // datetime-local gives us "YYYY-MM-DDTHH:mm" without timezone;
  // interpret it as the operator's local timezone and convert
  // to a UTC ISO string. Date.parse with no TZ assumes local.
  return new Date(local).toISOString();
}

export function AdventureNewPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const teams = useQuery({
    queryKey: ['teams'],
    queryFn: () => api<{ teams: { id: string; name: string }[] }>('/api/teams'),
  });

  const [teamId, setTeamId] = useState<string>('');
  const [name, setName] = useState<string>('');
  // '' = "Surprise me" → server assigns a random venue at start.
  const [place, setPlace] = useState<string>('');
  const [jiraSprintId, setJiraSprintId] = useState<string>('');
  const [startsAt, setStartsAt] = useState<string>(defaultStart());
  const [endsAt, setEndsAt] = useState<string>(defaultEnd());
  const [graceDays, setGraceDays] = useState<number>(0);
  const [commitmentText, setCommitmentText] = useState<string>('');

  // Phase 1.5: import from Jira. The sprints query only runs
  // when the operator has picked a team (and the team has a
  // Jira ingestor on the server side; otherwise the query
  // returns a 404 and we show a hint).
  const sprints = useQuery({
    queryKey: ['jira-sprints', teamId],
    queryFn: () => adventuresApi.listJiraSprints(teamId),
    enabled: !!teamId,
    retry: false,  // 404 should not retry
  });
  const [selectedSprintId, setSelectedSprintId] = useState<string>('');
  const preview = useQuery({
    queryKey: ['jira-sprint-preview', teamId, selectedSprintId],
    queryFn: () => adventuresApi.previewFromJira(teamId, selectedSprintId),
    enabled: !!teamId && !!selectedSprintId,
    retry: false,
  });

  const create = useMutation({
    mutationFn: () => {
      // Parse the commitment textarea. Format: one ticket per
      // line, "KEY | TYPE | SUMMARY". Phase 1 doesn't enforce
      // Jira keys; the operator can type whatever they want
      // (including "MIGRATION-PLAN" for non-Jira commitments).
      // The api's CommitmentTicketSchema validates key/type/
      // summary shape; failures bubble up as 400s.
      const commitment: AdventureCommitmentTicket[] = commitmentText
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .map((line) => {
          const parts = line.split('|').map((p) => p.trim());
          const [key = '', type = 'task', summary = ''] = parts;
          return {
            key,
            type,
            summary: summary || type,
            points_at_snapshot: null,
            actor_external: null,
          };
        });
      return adventuresApi.create({
        team_id: teamId,
        name,
        jira_sprint_id: jiraSprintId || null,
        starts_at: localToIso(startsAt),
        ends_at: localToIso(endsAt),
        completion_grace_days: graceDays,
        commitment,
        place: place || null,
      });
    },
    onSuccess: ({ adventure }) => {
      void qc.invalidateQueries({ queryKey: ['admin-adventures'] });
      navigate(`/admin/adventures/${adventure.id}`);
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    create.mutate();
  };

  const apiError = create.error instanceof ApiError ? create.error : null;
  const validationIssues = apiError?.body && typeof apiError.body === 'object' && 'issues' in apiError.body
    ? (apiError.body.issues as Array<{ path: string[]; message: string }>)
    : undefined;

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-ink">New adventure</h1>
        <Link to="/admin/adventures" className="text-sm text-ink-muted hover:text-ink">
          ← Back to list
        </Link>
      </div>

      <form onSubmit={submit} className="space-y-6 bg-bg-elev border border-border rounded p-6">
        <JiraImportPanel
          teamId={teamId}
          sprints={sprints.data?.sprints ?? null}
          sprintsPending={sprints.isPending}
          sprintsError={sprints.error as ApiError | null}
          selectedSprintId={selectedSprintId}
          onSelectSprint={setSelectedSprintId}
          preview={preview.data ?? null}
          previewPending={preview.isPending}
          previewError={preview.error as ApiError | null}
          onApplyPreview={(sprint, tickets) => {
            // Fill the form from the preview.
            setName((n) => n || `${sprint.name} (${sprint.state})`);
            setJiraSprintId(sprint.id);
            if (sprint.start_date) setStartsAt(toLocalInput(new Date(sprint.start_date)));
            if (sprint.end_date) setEndsAt(toLocalInput(new Date(sprint.end_date)));
            setCommitmentText(
              tickets
                .map((t) => `${t.key} | ${t.type} | ${t.summary}${t.points_at_snapshot != null ? ` (${t.points_at_snapshot} pts)` : ''}`)
                .join('\n'),
            );
          }}
        />
        <div>
          <label className="block text-sm font-medium text-ink mb-1">Team</label>
          <select
            required
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
            className="w-full px-3 py-2 bg-bg-base border border-border rounded"
          >
            <option value="" disabled>Pick a team…</option>
            {(teams.data?.teams ?? []).map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-ink mb-1">Name</label>
          <input
            required
            maxLength={200}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Sprint 47 — auth flow migration"
            className="w-full px-3 py-2 bg-bg-base border border-border rounded"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-ink mb-1">Venue (sprint title)</label>
          <select
            value={place}
            onChange={(e) => setPlace(e.target.value)}
            className="w-full px-3 py-2 bg-bg-base border border-border rounded"
          >
            <option value="">🎲 Surprise me — random venue at start</option>
            {ADVENTURE_PLACES.map((p) => (
              <option key={p} value={p}>The {p}</option>
            ))}
          </select>
          <p className="text-xs text-ink-muted mt-1">
            The public board title becomes "The {place || '…'} of {name || '…'}". Pick a venue, or leave it to chance.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-ink mb-1">Starts at</label>
            <input
              required
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              className="w-full px-3 py-2 bg-bg-base border border-border rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1">Ends at</label>
            <input
              required
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              className="w-full px-3 py-2 bg-bg-base border border-border rounded"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-ink mb-1">Jira sprint id (optional)</label>
            <input
              value={jiraSprintId}
              onChange={(e) => setJiraSprintId(e.target.value)}
              placeholder="e.g. 12345 or sprint_name"
              className="w-full px-3 py-2 bg-bg-base border border-border rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1">
              Completion grace days
              <span className="ml-2 text-xs text-ink-muted font-normal">(0 = strict)</span>
            </label>
            <input
              type="number"
              min={0}
              max={7}
              value={graceDays}
              onChange={(e) => setGraceDays(Number(e.target.value))}
              className="w-full px-3 py-2 bg-bg-base border border-border rounded"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-ink mb-1">
            Commitment (optional)
            <span className="ml-2 text-xs text-ink-muted font-normal">
              one ticket per line: <code>KEY | TYPE | SUMMARY</code>
            </span>
          </label>
          <textarea
            value={commitmentText}
            onChange={(e) => setCommitmentText(e.target.value)}
            rows={6}
            placeholder={'YOLO-101 | feature | Land the auth flow\nYOLO-102 | bug | Fix the migration crash'}
            className="w-full px-3 py-2 bg-bg-base border border-border rounded font-mono text-sm"
          />
          <p className="text-xs text-ink-muted mt-1">
            Phase 1 captures the commitment snapshot at create time. Goal authoring arrives in Phase 2.
          </p>
        </div>

        {apiError && (
          <div className="px-3 py-2 bg-red-900/30 border border-red-700 text-red-200 text-sm rounded">
            {(apiError.body as { error?: string } | null)?.error === 'invalid_body' && validationIssues ? (
              <ul className="list-disc list-inside space-y-1">
                {validationIssues.map((issue, i) => (
                  <li key={i}><code>{issue.path.join('.')}</code>: {issue.message}</li>
                ))}
              </ul>
            ) : (
              <span>{(apiError.body as { error?: string } | null)?.error ?? apiError.message}</span>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-3">
          <Link to="/admin/adventures" className="px-4 py-2 text-sm text-ink-muted hover:text-ink">
            Cancel
          </Link>
          <button
            type="submit"
            disabled={create.isPending || !teamId || !name}
            className="px-4 py-2 bg-brand text-white rounded hover:bg-brand-dark transition disabled:opacity-50"
          >
            {create.isPending ? 'Creating…' : 'Create draft'}
          </button>
        </div>
      </form>
    </div>
  );
}

// Adventury venues for the sprint title (mirrors the API's list, which
// picks one at random when the admin leaves it unset).
const ADVENTURE_PLACES = [
  'Maze', 'Arena', 'Halls', 'Labyrinth', 'Citadel', 'Archives', 'Forge',
  'Caverns', 'Sanctum', 'Spire', 'Observatory', 'Bastion', 'Vault',
  'Undergrove', 'Workshop', 'Library', 'Crossroads', 'Colosseum',
];

// ---- JiraImportPanel ----
//
// Phase 1.5 (ADR-0024): lets the operator import a Jira sprint
// as the adventure's commitment. Shows a sprint picker that
// auto-fills the form fields when the operator previews a
// sprint.
//
// State machine:
//   1. Team not picked           -> panel hidden
//   2. Team picked, no sprints   -> 'no_jira_ingestor' hint
//   3. Sprints loading           -> spinner
//   4. Sprints loaded            -> picker (active first)
//   5. Sprint picked             -> preview (commitment list)
//   6. "Use this sprint" clicked -> fill form fields, hide panel
//
// The panel does NOT submit the form; it just fills fields.
// Manual authoring below the panel still works for teams without
// a Jira ingestor, or when the operator wants to author without
// a sprint.

interface JiraImportPanelProps {
  teamId: string;
  sprints: JiraSprintSummary[] | null;
  sprintsPending: boolean;
  sprintsError: ApiError | null;
  selectedSprintId: string;
  onSelectSprint: (id: string) => void;
  preview: {
    sprint: JiraSprintSummary;
    tickets: Array<{ key: string; type: string; summary: string; points_at_snapshot: number | null }>;
    grace_days_suggestion: number;
  } | null;
  previewPending: boolean;
  previewError: ApiError | null;
  onApplyPreview: (
    sprint: JiraSprintSummary,
    tickets: Array<{ key: string; type: string; summary: string; points_at_snapshot: number | null }>,
  ) => void;
}

function JiraImportPanel(props: JiraImportPanelProps) {
  if (!props.teamId) return null;

  const errorBody = (e: ApiError | null): string | null => {
    if (!e) return null;
    const body = e.body as { error?: string; message?: string } | null;
    return body?.message ?? body?.error ?? e.message;
  };

  // The 404 / 503 from the api are not 'real' errors — they're
  // signals the operator sees ("this team has no Jira ingestor"
  // / "set the sprint field"). Surface them as inline hints, not
  // red error banners.
  const sprintsErrorBody = (props.sprintsError?.body as { error?: string } | null)?.error;
  const isIngestorMissing = sprintsErrorBody === 'no_jira_ingestor';
  const isSprintFieldMissing = sprintsErrorBody === 'sprint_field_not_configured';

  return (
    <div className="bg-bg-base border border-border rounded p-4 space-y-3">
      <div>
        <h2 className="text-sm font-medium text-ink">Import from Jira</h2>
        <p className="text-xs text-ink-muted mt-1">
          Pick a recent sprint to auto-fill the commitment list + dates. Manual authoring below still works.
        </p>
      </div>

      {props.sprintsPending && (
        <div className="text-sm text-ink-muted">Loading sprints…</div>
      )}

      {isIngestorMissing && (
        <div className="text-xs text-amber-300 bg-amber-900/20 border border-amber-700/40 rounded px-3 py-2">
          No enabled Jira ingestor for this team. Manual authoring only — or set up a Jira ingestor in <Link to="/admin/ingestors" className="underline">Ingestors</Link>.
        </div>
      )}

      {isSprintFieldMissing && (
        <div className="text-xs text-amber-300 bg-amber-900/20 border border-amber-700/40 rounded px-3 py-2">
          The team's Jira ingestor doesn't have <code>sprintField</code> configured. Set it in the ingestor config (e.g. <code>customfield_10020</code>) and refresh.
        </div>
      )}

      {props.sprintsError && !isIngestorMissing && !isSprintFieldMissing && (
        <div className="text-xs text-red-300 bg-red-900/20 border border-red-700/40 rounded px-3 py-2">
          {errorBody(props.sprintsError)}
        </div>
      )}

      {props.sprints && props.sprints.length === 0 && (
        <div className="text-xs text-ink-muted">
          No sprints with issues found for this team. Manual authoring only.
        </div>
      )}

      {props.sprints && props.sprints.length > 0 && (
        <div className="space-y-2">
          <select
            value={props.selectedSprintId}
            onChange={(e) => props.onSelectSprint(e.target.value)}
            className="w-full px-3 py-2 bg-bg-elev border border-border rounded text-sm"
          >
            <option value="">Pick a sprint to preview…</option>
            {props.sprints.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.state}, {s.issue_count} ticket{s.issue_count === 1 ? '' : 's'})
              </option>
            ))}
          </select>

          {props.previewPending && (
            <div className="text-sm text-ink-muted">Loading preview…</div>
          )}

          {props.preview && (
            <div className="bg-bg-elev border border-border rounded p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-ink">{props.preview.sprint.name}</div>
                  <div className="text-xs text-ink-muted">
                    {props.preview.sprint.start_date && (
                      <>{new Date(props.preview.sprint.start_date).toLocaleDateString()} → </>
                    )}
                    {props.preview.sprint.end_date && (
                      <>{new Date(props.preview.sprint.end_date).toLocaleDateString()}</>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => props.onApplyPreview(props.preview!.sprint, props.preview!.tickets)}
                  className="px-3 py-1.5 bg-brand text-white text-sm rounded hover:bg-brand-dark transition"
                >
                  Use this sprint
                </button>
              </div>
              <div className="text-xs text-ink-muted">
                {props.preview.tickets.length} ticket{props.preview.tickets.length === 1 ? '' : 's'}
                {props.preview.tickets.some((t) => t.points_at_snapshot == null) && (
                  <> · {props.preview.tickets.filter((t) => t.points_at_snapshot == null).length} without points estimate</>
                )}
              </div>
              <ul className="text-xs font-mono space-y-0.5 max-h-32 overflow-y-auto">
                {props.preview.tickets.slice(0, 10).map((t) => (
                  <li key={t.key} className="text-ink-muted">
                    {t.key} <span className="opacity-60">{t.type}</span> {t.summary}
                  </li>
                ))}
                {props.preview.tickets.length > 10 && (
                  <li className="text-ink-muted/60">… and {props.preview.tickets.length - 10} more</li>
                )}
              </ul>
            </div>
          )}

          {props.previewError && (
            <div className="text-xs text-red-300">
              Preview failed: {errorBody(props.previewError)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
